import { cliengoQueries } from '@cliengo/mfe-utils/hooks';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PriceChangeOutlinedIcon from '@mui/icons-material/PriceChangeOutlined';
import { Box, Popover, Skeleton } from '@mui/material';
import { useEffect, useState } from 'react';
import ApartmentOutlined from '@mui/icons-material/ApartmentOutlined';
import Logout from '@mui/icons-material/Logout';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import { useLocation } from 'react-router-dom';
import { usePermissionsService } from '@cliengo/mfe-utils/services';

import { Show } from '../../utils';
import { useNavigationContext } from '../Navigation';

import { TriggerButton } from './TriggerButton';
import { PopoverHeader } from './PopoverHeader';
import { PopoverSection } from './PopoverSection';
import { CopyIDButton, PopoverItem } from './PopoverItem';
import { CardIdIcon } from './CardIdIcon';

import { StyledTooltip } from '@/ui/Tooltip/Tooltip';
import * as track from '@/utils/eventTracker';
import environment from '@/config/environment';
import { logout } from '@/utils/auth';
import { useTranslate } from '@/hooks';

export const ProfileButton = () => {
  const { isSmall } = useNavigationContext();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { data: user, status: userQueryStatus } = cliengoQueries.user();
  const { data: account, status: accountQueryStatus } =
    cliengoQueries.account();
  const permissions = usePermissionsService();
  const location = useLocation();
  const isInternalUser =
    user?.email?.includes('@cliengo.com') ||
    user?.email?.includes('@leadaki.com');

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'avatar-popover' : undefined;
  const { t } = useTranslate('profileButtom');

  useEffect(() => {
    handleClose();
  }, [location]);

  if (userQueryStatus !== 'success' || accountQueryStatus !== 'success') {
    return <Skeleton variant="circular" width={40} height={40} />;
  }

  return (
    <div>
      <Show when={isSmall}>
        <TriggerButton id={id} onClick={handleClick} user={user} />
      </Show>
      <Show when={!isSmall}>
        <StyledTooltip title={t('myAccount')}>
          <TriggerButton id={id} onClick={handleClick} user={user} />
        </StyledTooltip>
      </Show>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          maxWidth: '280px',
        }}
      >
        <PopoverHeader user={user} />
        <PopoverSection>
          <Show when={permissions.isAdmin()}>
            <PopoverItem
              to="/account/my-company"
              event={track.goToCompany}
              startAdornment={<ApartmentOutlined />}
            >
              {t('myCompany')}
            </PopoverItem>
          </Show>
          <PopoverItem
            to="/account/my-profile"
            event={track.goToProfile}
            startAdornment={<CardIdIcon />}
          >
            {t('myProfile')}
          </PopoverItem>
        </PopoverSection>
        <Show when={permissions.isAdmin()}>
          <PopoverSection>
            <PopoverItem
              to="/account/plans"
              event={track.goToPlans}
              startAdornment={<PriceChangeOutlinedIcon />}
            >
              {t('plans')}
            </PopoverItem>
            <PopoverItem
              to="/account/billing"
              event={track.goToBilling}
              startAdornment={<ReceiptLongOutlined />}
            >
              {t('billing')}
            </PopoverItem>
          </PopoverSection>
        </Show>
        <Show when={isInternalUser}>
          <PopoverSection>
            <PopoverItem
              to={`${environment.CLIENGO_APP_URL}/admin/companies`}
              startAdornment={<AdminPanelSettingsIcon />}
            >
              Admin
            </PopoverItem>
          </PopoverSection>
        </Show>
        <PopoverSection>
          <CopyIDButton companyId={account?.id} />
        </PopoverSection>
        <Box sx={{ pt: '10px' }}>
          <PopoverItem
            isLogout
            event={logout}
            to="/home"
            startAdornment={<Logout />}
          >
            {t('logOut')}
          </PopoverItem>
        </Box>
      </Popover>
    </div>
  );
};
